import type { Affix, AffixDef, AffixId, MasterDie } from './types';
import { AFFIX_BY_ID, AFFIX_POOL_WEIGHT, affixPoolFor } from '../data/affixes';
import { BALANCE } from '../data/balance';
import type { Rng } from './rng';

/** Arrondit une valeur tirée au pas de l'affixe (évite les 0,17000000000000004). */
function quantize(value: number, step: number): number {
  const decimals = Math.max(0, Math.ceil(-Math.log10(step) + 1e-9));
  return Number((Math.round(value / step) * step).toFixed(decimals));
}

/** Tire la valeur X d'un affixe dans sa plage. */
export function rollAffixValue(def: AffixDef, rng: Rng): number {
  if (def.min === def.max) return def.min;
  const raw = rng.float(def.min, def.max + def.step * 0.999);
  const v = quantize(raw, def.step);
  return Math.min(def.max, Math.max(def.min, v));
}

/**
 * Tire `count` affixes pour un dé à `sides` faces.
 *
 * Le même affixe peut sortir plusieurs fois (spec §5.4) : le pool n'est pas
 * consommé — **sauf** pour les affixes marqués `unique` (*Pipé*, COMMENTS
 * v0.6.0 §1.3), retirés du pool dès qu'ils sont sortis une fois.
 */
export function rollAffixes(sides: number, count: number, rng: Rng): Affix[] {
  const pool = affixPoolFor(sides);
  const out: Affix[] = [];
  const taken = new Set<AffixId>();
  for (let i = 0; i < count; i++) {
    const choices = pool.filter((d) => !(d.unique && taken.has(d.id)));
    const def = rng.weighted(choices, (d) => AFFIX_POOL_WEIGHT[d.pool]);
    if (!def) break;
    if (def.unique) taken.add(def.id);
    out.push({ id: def.id, value: rollAffixValue(def, rng) });
  }
  return out;
}

/** Nombre d'instances d'un affixe sur un dé. */
export function affixCount(die: MasterDie | null | undefined, id: AffixId): number {
  if (!die) return 0;
  let n = 0;
  for (const a of die.affixes) if (a.id === id) n += 1;
  return n;
}

/**
 * Valeur **effective** d'un affixe porté par `die` : sa valeur tirée, × 1,5
 * si le dé est **Enrichi** (v0.46.0, A94). C'est la valeur que le combat
 * applique et que l'interface affiche (`Game.affixValueOf`).
 */
export function affixValueOf(die: Pick<MasterDie, 'enriched'> | null | undefined, affix: Affix): number {
  return die?.enriched === true ? affix.value * BALANCE.enrichedAffixFactor : affix.value;
}

/**
 * Somme des valeurs d'un affixe porté par un dé (0 s'il ne le porte pas).
 * v0.46.0 (A94) : × 1,5 sur un dé **Enrichi** — *Lesté*, *Incisif*, *Précis*,
 * *Exécutant*, *Temporel*, *Doré*, *Empoisonné*, *Mutilant* ; *Avantageux*
 * reste un drapeau (valeur > 0), *Pipé* et les affixes nommés n'ont pas de
 * valeur utile.
 */
export function affixValue(die: MasterDie | null | undefined, id: AffixId): number {
  if (!die) return 0;
  let total = 0;
  for (const a of die.affixes) if (a.id === id) total += a.value;
  return die.enriched === true ? total * BALANCE.enrichedAffixFactor : total;
}

export function hasAffix(die: MasterDie | null | undefined, id: AffixId): boolean {
  if (!die) return false;
  return die.affixes.some((a) => a.id === id);
}

/** Somme d'un affixe sur un ensemble de dés (Doré / Temporel d'un lancer). */
export function sumAffix(dice: readonly (MasterDie | null)[], id: AffixId): number {
  let total = 0;
  for (const d of dice) total += affixValue(d, id);
  return total;
}

/**
 * Affixe « dominant » d'un dé : celui dont la somme des valeurs est la plus
 * forte relativement à sa plage. Sert à nommer le dé.
 */
export function dominantAffix(die: MasterDie): AffixDef | null {
  const scores = new Map<AffixId, number>();
  for (const a of die.affixes) {
    const def = AFFIX_BY_ID[a.id];
    const span = def.max > 0 ? def.max : 1;
    scores.set(a.id, (scores.get(a.id) ?? 0) + a.value / span);
  }
  let best: AffixId | null = null;
  let bestScore = -1;
  for (const [id, score] of scores) {
    if (score > bestScore) {
      bestScore = score;
      best = id;
    }
  }
  return best ? AFFIX_BY_ID[best] : null;
}
